// ─────────────────────────────────────────────────────────────────────────────
// Footer — the user-facing sitemap.
//
// Mirrors the five primary navigation menus in
// src/components/sections/_data/nav.ts (Platform · Solutions ·
// Intelligence · Resources · Company) plus a Legal column. When the nav
// changes, change this in the same pass — the two are not allowed to drift.
//
// Every href here resolves to a real route. /pricing is deliberately absent:
// it is hidden by decision and excluded from src/app/sitemap.ts as well.
// ─────────────────────────────────────────────────────────────────────────────
import Image from "next/image";
import Link from "next/link";

import CookieSettingsLink from "@/components/CookieSettingsLink";

export default function Footer() {
  return (
    <>
<footer>
  <div className="fg">

    <div className="fg-brand">
      <div className="fb" style={{display:"flex",alignItems:"center"}}>
        {/* Both axes are pinned in px (not width:"auto") so the rendered size
            always matches the width/height attributes. next/image serves a
            128px-wide variant whose rounded aspect ratio differs slightly from
            the 1165x434 source, and "auto" made Next's dev check flag the
            sub-pixel drift as "width or height modified, but not the other". */}
        <Image alt="LexVio.ai" height={36} src="/logo.png" style={{display:"block",height:"36px",width:"97px"}} width={97} />
      </div>
      <p className="ft">The Global AI Legal Operating System. One app, one login, one subscription.</p>
      <p className="fp">Headquartered in Delhi.</p>
      <p className="fp">A product of <a href="https://www.globalsynapsetech.com" rel="noopener noreferrer" target="_blank">Global Synapse Technologies</a></p>
    </div>

    <div>
      <div className="fc-t">Platform</div>
      <ul className="fc-l">
        <li><Link href="/product" prefetch={false}>Platform overview</Link></li>
        <li><Link href="/product/legal-ai" prefetch={false}>Legal AI</Link></li>
        <li><Link href="/product/compliance-ai" prefetch={false}>Compliance AI</Link></li>
        <li><Link href="/product/workflow-agents" prefetch={false}>Workflow Agents</Link></li>
        {/* The September 2026 work layer — mirrors the Platform menu's Work column. */}
        <li><Link href="/features/meeting-intelligence" prefetch={false}>Meeting Intelligence</Link></li>
        <li><Link href="/features/tasks" prefetch={false}>Tasks</Link></li>
        <li><Link href="/features/contract-lifecycle" prefetch={false}>Contract lifecycle</Link></li>
        <li><Link href="/features/mobile-app" prefetch={false}>Mobile app (in testing)</Link></li>
        <li><Link href="/features" prefetch={false}>All features</Link></li>
        <li><Link href="/integrations" prefetch={false}>Integrations</Link></li>
        <li><Link href="/security" prefetch={false}>Security</Link></li>
        <li><Link href="/scan" prefetch={false}>Free scanner</Link></li>
      </ul>
    </div>

    <div>
      <div className="fc-t">Solutions</div>
      <ul className="fc-l">
        <li><Link href="/solutions/law-firms" prefetch={false}>Law firms</Link></li>
        <li><Link href="/solutions/advocates" prefetch={false}>Advocates</Link></li>
        <li><Link href="/solutions/enterprise" prefetch={false}>Enterprise</Link></li>
        <li><Link href="/solutions/financial-institutions" prefetch={false}>Financial institutions</Link></li>
        <li><Link href="/solutions/smb" prefetch={false}>Small businesses</Link></li>
        <li><Link href="/solutions/founders" prefetch={false}>Founders</Link></li>
        <li><Link href="/solutions/agencies" prefetch={false}>Agencies</Link></li>
        <li><Link href="/solutions/freelancers" prefetch={false}>Freelancers</Link></li>
        <li><Link href="/solutions/students" prefetch={false}>Students</Link></li>
        <li><Link href="/solutions" prefetch={false}>All solutions</Link></li>
      </ul>
    </div>

    <div>
      <div className="fc-t">Intelligence</div>
      <ul className="fc-l">
        <li><Link href="/features/vio-ask" prefetch={false}>AI legal assistant</Link></li>
        {/* Legal research AI, Citation intelligence and Legal simulators
            removed 2026-09-05: all three sit on India-only backend surfaces
            (the judgments corpus and the simulator pack, both mounted
            `require_india_edition`), so their /features pages are now 404 here
            — see features/_data/features.ts. Citation verification below is
            the check that does run on this product. */}
        <li><Link href="/features/citation-verification" prefetch={false}>Citation verification</Link></li>
        <li><Link href="/features/governing-law-detection" prefetch={false}>Governing law detection</Link></li>
        <li><Link href="/features/clause-risk-scoring" prefetch={false}>Contract AI</Link></li>
        <li><Link href="/features/legal-health-score" prefetch={false}>Legal Health Score</Link></li>
        <li><Link href="/features/nexus-search" prefetch={false}>Portfolio search</Link></li>
        {/* Regulator monitoring removed 2026-09-05 — `/api/v1/compliance` is
            India-gated and the regulators behind it are the ones
            `sebi_dashboard` names in INDIA_ONLY_FEATURE_KEYS. */}
        <li><Link href="/features/m-and-a-intelligence" prefetch={false}>M&amp;A intelligence</Link></li>
        <li><Link href="/features/no-training-on-your-data" prefetch={false}>AI controls</Link></li>
      </ul>
    </div>

    <div>
      <div className="fc-t">Resources</div>
      <ul className="fc-l">
        {/* Template library and Legal glossary removed 2026-09-02: both are
            India-only content (§1A) and are now 404 on this site — see
            lib/edition/indiaOnlyRoutes.ts. Restore each link when a non-India
            version of that content exists, not before. */}
        <li><Link href="/blog" prefetch={false}>Blog</Link></li>
        <li><Link href="/features/community-blog" prefetch={false}>Write for the blog</Link></li>
        <li><Link href="/faqs" prefetch={false}>FAQs</Link></li>
        {/* Roadmap and Changelog restored 2026-09-18 — real pages now, same as
            the Resources menu's Product column. */}
        <li><Link href="/roadmap" prefetch={false}>Roadmap</Link></li>
        <li><Link href="/changelog" prefetch={false}>Changelog</Link></li>
        <li><Link href="/status" prefetch={false}>Status</Link></li>
      </ul>
    </div>

    <div>
      <div className="fc-t">Company</div>
      <ul className="fc-l">
        <li><Link href="/about" prefetch={false}>About</Link></li>
        <li><Link href="/why-lexvio" prefetch={false}>Why LexVio</Link></li>
        <li><Link href="/team" prefetch={false}>Team</Link></li>
        <li><Link href="/partners" prefetch={false}>Partners</Link></li>
        <li><Link href="/careers" prefetch={false}>Careers</Link></li>
        <li><Link href="/contact" prefetch={false}>Contact</Link></li>
        <li><Link href="/press" prefetch={false}>Press</Link></li>
        <li><Link href="/investors" prefetch={false}>Investors</Link></li>
        <li><Link href="/trust" prefetch={false}>Trust center</Link></li>
        <li><Link href="/book-demo" prefetch={false}>Book a demo</Link></li>
      </ul>
    </div>

  </div>
  <div className="fbot">
    <div>
      <div className="fcopy">© 2026 LexVio. All rights reserved.</div>
      <div className="fcopy" style={{marginTop:"3px"}}>Registered in Delhi, India · <a href="mailto:info@globalsynapsetech.com">info@globalsynapsetech.com</a></div>
      {/* Policy links live in the bottom bar rather than as a seventh column —
          it is where readers look for them, and it buys back a column of width
          for the sections people actually navigate to. */}
      <div className="flegal">
        <Link href="/legal/privacy" prefetch={false}>Privacy</Link>
        <Link href="/legal/terms" prefetch={false}>Terms</Link>
        <Link href="/legal/refund" prefetch={false}>Refund</Link>
        <Link href="/legal/dpa" prefetch={false}>DPA</Link>
        <Link href="/legal/grievance" prefetch={false}>Grievance</Link>
        <Link href="/legal/cookies" prefetch={false}>Cookies</Link>
        <CookieSettingsLink />
      </div>
    </div>
    <div className="fsocs">
      <a className="fsoc" href="https://x.com/lexvioai" rel="noopener noreferrer" target="_blank">Twitter</a>
      <a className="fsoc" href="https://linkedin.com/company/lexvioai" rel="noopener noreferrer" target="_blank">LinkedIn</a>
      <a className="fsoc" href="https://youtube.com/@lexvioai" rel="noopener noreferrer" target="_blank">YouTube</a>
      <a className="fsoc" href="https://wa.me/919205757755" rel="noopener noreferrer" target="_blank">WhatsApp</a>
    </div>
  </div>
</footer>
    </>
  );
}
